import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import styled from '@emotion/styled';
import { generatePath, useNavigate, useSearchParams, useParams } from 'react-router';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { rio } from '@amzn/rio-design-tokens';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import { StarRating } from 'src/common/starRating/StarRating';
import { DealType } from '@amzn/handshake-painter-typescript-client/v1';
import { RioText, DealText, DiscountText, TruncatedParagraph, Paragraph } from 'src/common/styled';
import { PrimeBadge } from 'src/common/icons';
import { LargePrice } from 'src/common/largePrice/LargePrice';
import { parsePriceComponents, formatPrice } from 'src/utils/priceUtils';
import { YellowButton } from '../buttons';
import { routes } from 'src/utils/routes';
import { addAsinToCreatorAttributionToken, getAsinFromCreatorAttributionToken, } from 'src/utils/creatorAttributionUtils';
import { useCreatorAttributionStore } from 'src/store';
import { usePageContext } from 'src/hooks';
const CardImage = styled.img `
  // Retain aspect ratio by height
  width: auto;
  height: 100%;
  // Control max dimensions
  max-width: 100%;
`;
const ImageContainer = styled.div `
  background: ${rio.semantic.color.background.primary};
  border-radius: 8px;
  overflow: hidden;

  height: ${({ isStorefront }) => (isStorefront ? '150px' : '136px')};
  display: flex;
  justify-content: center;
`;
export const ProductCard = ({ productItem, isStorefront = false, storefrontAsins, activeCategory, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { aci } = useParams();
    const { getCreatorAttributionToken, setCreatorAttributionToken } = useCreatorAttributionStore();
    const { localizationContext } = useLocalizationContext();
    const locale = localizationContext.getLocale();
    const { commonBundle: bundle } = usePageContext();
    const onClick = () => {
        // Handle navigation
        const currentParams = new URLSearchParams(searchParams);
        if (isStorefront && aci) {
            // Set ACI in current parameters
            currentParams.set('aci', aci);
            // Handle ASIN token logic
            const currentToken = getCreatorAttributionToken();
            const currentAsin = getAsinFromCreatorAttributionToken(currentToken);
            if (currentAsin !== productItem.asin) {
                const newToken = addAsinToCreatorAttributionToken(currentToken, productItem.asin);
                setCreatorAttributionToken(newToken);
            }
        }
        if (storefrontAsins) {
            currentParams.set('asins', storefrontAsins);
        }
        // Stamp storefront history entry with tab + scroll state before navigating
        if (activeCategory) {
            navigate(window.location.pathname + window.location.search, {
                replace: true,
                state: { activeCategory, scrollY: window.scrollY, preventScrollReset: true },
            });
        }
        const dpRoute = generatePath(routes.productDetailPage, { asin: productItem.asin });
        navigate(`${dpRoute}?${currentParams.toString()}`);
    };
    const imageUrl = ((_b = (_a = productItem.images.imageList) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.highRes) || ((_d = (_c = productItem.images.imageList) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.lowRes) || '';
    const imageAlt = productItem.images.altText || productItem.title;
    // Parse price components for LargePrice component
    const priceComponents = productItem.price && !productItem.price.shouldHidePrice
        ? parsePriceComponents(productItem.price.amount, productItem.price.currencyCode, locale)
        : null;
    return (_jsx("div", { onClick: onClick, "data-testid": "product-card", children: _jsxs(Column, { spacing: "200", width: isStorefront ? '100%' : '136px', children: [_jsx(ImageContainer, { isStorefront: isStorefront, children: _jsx(CardImage, { loading: isStorefront ? 'eager' : 'lazy', src: imageUrl, alt: imageAlt, "data-testid": "product-card-image" }) }), _jsx(TruncatedParagraph, { clamp: 2, type: "b200", "data-testid": "product-card-title", children: productItem.title }), _jsxs(Row, { spacing: "200", alignmentVertical: "center", children: [_jsx(StarRating, { rating: ((_e = productItem.reviews) === null || _e === void 0 ? void 0 : _e.rating) || 0 }), ((_f = productItem.reviews) === null || _f === void 0 ? void 0 : _f.count) && (_jsxs(RioText, { rioColor: "secondary", type: "b100", children: ["(", productItem.reviews.count, ")"] }))] }), !productItem.buyable ? (_jsx(RioText, { rioColor: "error", type: "b200", truncate: true, children: bundle.getMessage('currentlyUnavailable') })) : (_jsxs(_Fragment, { children: [_jsxs(Row, { spacing: "200", alignmentVertical: "center", children: [((_g = productItem.deal) === null || _g === void 0 ? void 0 : _g.discountPercentage) && (_jsxs(DiscountText, { type: "b500", children: ["-", productItem.deal.discountPercentage, "%"] })), priceComponents && (_jsx(LargePrice, { size: "small", currencySymbol: priceComponents.currencySymbol, basePart: priceComponents.basePart, fractionalPart: priceComponents.fractionalPart, currencySymbolPosition: priceComponents.currencySymbolPosition }))] }), productItem.deal && productItem.deal.type !== DealType.None && (_jsx(DealText, { type: "b100", children: _jsx("b", { children: bundle.getMessage('deal') }) })), productItem.deal && productItem.originalPrice && !productItem.originalPrice.shouldHidePrice && (_jsx(RioText, { type: "b200", children: bundle.formatMessage('listPrice', {
                                price: formatPrice(productItem.originalPrice.amount, productItem.originalPrice.currencyCode, locale),
                                s: (chunk) => _jsx("s", { children: chunk }, chunk),
                            }) })), ((_h = productItem.prime) === null || _h === void 0 ? void 0 : _h.isPrime) && _jsx(PrimeBadge, {}), !!((_j = productItem.deliveryMessage) === null || _j === void 0 ? void 0 : _j.length) && (_jsx(Paragraph, { type: "b200", rioColor: "secondary", children: productItem.deliveryMessage.map((msg, index) => msg.isBold ? _jsx("b", { children: msg.text }, index) : _jsx("span", { children: msg.text }, index)) }))] })), isStorefront && productItem.buyable && (_jsx(YellowButton, { height: "small", size: "small", "data-testid": "product-card-see-more-button", children: bundle.getMessage('seeMore') }))] }) }));
};
